import { useEffect, useRef, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Layout } from "../components/Layout"
import { Button } from "@/components/Button"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { EmailBody } from "@/components/EmailBody"
import { ToastAction } from "@/components/Toast/Toast"
import { useToast } from "@/components/Toast"
import { client } from "@/api"
import {
  useCommsThread,
  useSetThreadRelevance,
  useSetThreadResolved,
  useAddThreadNote,
  useDeleteThreadNote,
  useReplyToThread,
  useGenerateDraft,
  useDeleteVoicemail,
  ThreadMessage,
  ThreadNote,
} from "@/hooks/useComms"
import {
  ArrowLeftIcon,
  PaperClipIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"

// Voicemail callers are stored under a `+1555…@phone.invalid` placeholder address
// (never a mailbox); show just the phone number.
function displayAddress(addr: string): string {
  return addr.replace(/@phone\.invalid$/i, "")
}

// Streams the private recording through the authenticated admin route (cookie auth via
// the RPC client), then plays it from a blob URL. The URL is revoked on unmount.
function VoicemailPlayer({ publicId }: { publicId: string }) {
  const [src, setSrc] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false
    setSrc(null)
    setFailed(false)
    client.admin.comms.messages[":publicId"].audio
      .$get({ param: { publicId } })
      .then((res) => {
        if (!res.ok) throw new Error(`audio ${res.status}`)
        return res.blob()
      })
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setSrc(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [publicId])

  if (failed) {
    return <p className="mb-2 text-xs text-muted-foreground">Recording unavailable.</p>
  }
  if (!src) {
    return <p className="mb-2 text-xs text-muted-foreground">Loading recording…</p>
  }
  return <audio controls src={src} className="mb-2 w-full" />
}

function MessageCard({
  m,
  onDeleteVoicemail,
}: {
  m: ThreadMessage
  onDeleteVoicemail: (publicId: string) => void
}) {
  const outbound = m.direction === "outbound"
  const when = m.sent_at ? new Date(m.sent_at).toLocaleString() : "unknown time"
  return (
    <div
      className={`rounded-lg border p-4 ${
        outbound ? "border-blue-500/40 bg-blue-500/10" : "border-border bg-surface"
      }`}
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span
            className={`mr-2 rounded-full px-2 py-0.5 text-xs font-medium ${
              outbound ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700"
            }`}
          >
            {outbound ? "sent" : "received"}
          </span>
          <span className="text-sm font-medium text-primary">{displayAddress(m.from_email)}</span>
          {m.to_emails.length > 0 && (
            <span className="ml-2 text-xs text-muted-foreground">→ {m.to_emails.join(", ")}</span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{when}</span>
      </div>

      {m.classification?.one_line_summary && (
        <p className="mb-2 text-xs italic text-muted-foreground">{m.classification.one_line_summary}</p>
      )}

      {m.has_audio && <VoicemailPlayer publicId={m.public_id} />}

      <EmailBody body={m.body_text} />

      <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="font-mono">{m.source}</span>
        {m.source === "twilio-voicemail" && (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => onDeleteVoicemail(m.public_id)}
          >
            <TrashIcon className="mr-1 h-3.5 w-3.5" /> Delete voicemail
          </Button>
        )}
        {m.has_attachments && (
          <span className="inline-flex items-center gap-1">
            <PaperClipIcon className="h-3.5 w-3.5" /> attachment
          </span>
        )}
        {m.cc_emails.length > 0 && <span>cc: {m.cc_emails.join(", ")}</span>}
      </div>
    </div>
  )
}

function NoteCard({ n, onDelete }: { n: ThreadNote; onDelete: () => void }) {
  const when = n.created_at ? new Date(n.created_at).toLocaleString() : "unknown time"
  return (
    <div className="rounded-lg border border-amber-400/50 bg-amber-50/60 p-4 dark:bg-amber-500/10">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-300">
          <PencilSquareIcon className="h-3.5 w-3.5" /> Note
          {n.author && <span className="font-normal text-muted-foreground">· {n.author}</span>}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{when}</span>
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete note"
            className="text-muted-foreground hover:text-destructive"
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <pre className="whitespace-pre-wrap break-words font-sans text-sm text-primary">{n.body}</pre>
    </div>
  )
}

// One timeline entry — a message or a note — sortable by its timestamp.
type TimelineItem =
  | { kind: "message"; at: string; data: ThreadMessage }
  | { kind: "note"; at: string; data: ThreadNote }

export function CommsThread() {
  const { publicId } = useParams<{ publicId: string }>()
  const { toast } = useToast()
  const { data, isLoading, error } = useCommsThread(publicId || "")
  const setRelevance = useSetThreadRelevance()
  const setResolved = useSetThreadResolved()
  const addNote = useAddThreadNote()
  const deleteNote = useDeleteThreadNote()
  const replyToThread = useReplyToThread()
  const generateDraft = useGenerateDraft()
  const deleteVoicemail = useDeleteVoicemail()
  const navigate = useNavigate()

  const [confirmNotRelevant, setConfirmNotRelevant] = useState(false)
  const [noteDraft, setNoteDraft] = useState("")
  const [replyDraft, setReplyDraft] = useState("")
  const [confirmReply, setConfirmReply] = useState(false)
  // public_id of the voicemail awaiting delete confirmation (null = dialog closed).
  const [confirmDeleteVoicemail, setConfirmDeleteVoicemail] = useState<string | null>(null)
  // Recipient the last generated draft was written for, bound to its thread so it sends
  // to whoever the draft answered (not a stale page-load recipient) and is ignored after
  // navigating to another thread (Codex #1141 P2). Cleared once a reply is sent.
  const [draftRecipient, setDraftRecipient] = useState<{
    threadId: string
    recipient: string | null
  } | null>(null)
  // Always-current route param, so an in-flight draft request that resolves after the
  // admin has navigated to another thread can be ignored (Codex #1141 P2).
  const currentThreadRef = useRef(publicId)
  currentThreadRef.current = publicId

  // This route component is reused across :publicId changes, so reset the composer when
  // the thread changes. Without this, a draft (or note) typed/generated on one thread
  // would linger in the next thread's composer and could be sent to its recipient
  // (Codex #1141 P2). Pairs with the in-flight guard above for drafts still generating.
  useEffect(() => {
    setReplyDraft("")
    setNoteDraft("")
    setDraftRecipient(null)
  }, [publicId])

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-1/3 rounded bg-muted" />
          <div className="h-32 rounded bg-muted" />
          <div className="h-32 rounded bg-muted" />
        </div>
      </Layout>
    )
  }

  const { thread, contact, messages, notes } = data
  const threadLabel = thread.subject || "(no subject)"
  const isQuarantined = thread.is_relevant === false
  const isResolved = thread.resolved_at != null

  // Merge messages + notes into one chronological timeline (oldest first). Items
  // without a timestamp sort to the end. Notes carry created_at; messages sent_at.
  const timeline: TimelineItem[] = [
    ...messages.map((m): TimelineItem => ({ kind: "message", at: m.sent_at ?? "", data: m })),
    ...notes.map((n): TimelineItem => ({ kind: "note", at: n.created_at ?? "", data: n })),
  ].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0))

  // Authoritative reply recipient from the backend (the true latest inbound sender,
  // computed from an untruncated query), not derived from the `messages` list, which
  // is capped at 200 and could miss the newest inbound on a long thread. After an AI
  // draft, prefer the recipient that draft was written for (bound to this thread) so the
  // reply text and the send target stay consistent even if a newer inbound has arrived.
  const recipientOverride =
    draftRecipient && draftRecipient.threadId === thread.public_id
      ? draftRecipient.recipient
      : null
  const replyRecipient = recipientOverride ?? data.reply_recipient

  const markNotRelevant = () => {
    setRelevance.mutate(
      { publicId: thread.public_id, relevant: false },
      {
        onSuccess: () => {
          toast({
            title: "Thread marked not relevant",
            description: "This thread is now hidden from comms views.",
            action: (
              <ToastAction
                altText="Restore this thread"
                onClick={() =>
                  setRelevance.mutate({ publicId: thread.public_id, relevant: true })
                }
              >
                Undo
              </ToastAction>
            ),
          })
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to mark not relevant. Please try again.",
            variant: "destructive",
          })
        },
      }
    )
  }

  const restore = () => {
    setRelevance.mutate(
      { publicId: thread.public_id, relevant: true },
      {
        onSuccess: () =>
          toast({ title: "Restored", description: "This thread is back in the active view." }),
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to restore. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  const toggleResolved = () => {
    const resolving = !isResolved
    setResolved.mutate(
      { publicId: thread.public_id, resolved: resolving },
      {
        onSuccess: () =>
          toast({
            title: resolving ? "Resolved" : "Reopened",
            description: resolving
              ? "Removed from owed-a-reply. It'll reopen automatically if they reply again."
              : "Back on the owed-a-reply list.",
          }),
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to update. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  const submitNote = () => {
    const body = noteDraft.trim()
    if (!body) return
    addNote.mutate(
      { publicId: thread.public_id, body },
      {
        onSuccess: () => setNoteDraft(""),
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to add the note. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  const removeNote = (notePublicId: string) => {
    deleteNote.mutate(
      { publicId: thread.public_id, notePublicId },
      {
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to delete the note. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  const doDeleteVoicemail = () => {
    const messagePublicId = confirmDeleteVoicemail
    if (!messagePublicId) return
    setConfirmDeleteVoicemail(null)
    deleteVoicemail.mutate(
      { publicId: messagePublicId },
      {
        onSuccess: (r) => {
          toast({
            title: "Voicemail deleted",
            description: "The recording and transcript were removed.",
          })
          // The thread went with its only message; there is nothing left to show.
          if (r.thread_deleted) navigate("/comms/threads")
        },
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to delete the voicemail. Nothing was deleted.",
            variant: "destructive",
          }),
      }
    )
  }

  const doReply = () => {
    const body = replyDraft.trim()
    if (!body || !replyRecipient) return
    setConfirmReply(false)
    replyToThread.mutate(
      { publicId: thread.public_id, body, to: replyRecipient },
      {
        onSuccess: (r) => {
          setReplyDraft("")
          setDraftRecipient(null)
          toast({
            title: "Reply sent",
            description: `Sent to ${r.to} from ${r.from}.`,
          })
        },
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to send the reply. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  // Generate an AI draft on demand and drop it into the composer. Always drafts against
  // the thread's current state; the human edits + sends it. If the composer already has
  // text, confirm before replacing so a generate-click never silently discards an edit.
  const doGenerateDraft = () => {
    if (replyDraft.trim() && !window.confirm("Replace the current draft with a new AI draft?")) {
      return
    }
    const forThread = thread.public_id
    generateDraft.mutate(
      { publicId: forThread },
      {
        onSuccess: (r) => {
          // Ignore a result that lands after the admin navigated to another thread, so a
          // draft for one conversation never appears in another's composer (Codex P2).
          if (currentThreadRef.current !== forThread) return
          setReplyDraft(r.draft)
          // Bind the send to the recipient the draft answered (see replyRecipient above).
          setDraftRecipient({ threadId: forThread, recipient: r.recipient })
        },
        onError: () =>
          toast({
            title: "Error",
            description: "Could not generate a draft. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <Link
          to={contact ? `/comms/contacts/${contact.public_id}` : "/comms"}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {contact ? contact.display_name || displayAddress(contact.primary_email) : "Back to triage"}
        </Link>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-primary">{threadLabel}</h1>
            <p className="text-muted-foreground">
              {contact ? contact.display_name || displayAddress(contact.primary_email) : "Unknown contact"}
              {contact?.org && <span> · {contact.org}</span>} · {thread.message_count} messages
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {thread.channel === "support" && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800">
                support
              </span>
            )}
            {thread.channel === "voicemail" && (
              <span className="inline-flex items-center rounded-full bg-violet-100 px-3 py-1 text-sm font-medium text-violet-800">
                voicemail
              </span>
            )}
            {isResolved ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
                <CheckCircleIcon className="h-4 w-4" /> Resolved
              </span>
            ) : (
              thread.still_owed && (
                <span className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800">
                  Owed a reply
                </span>
              )
            )}
            <Button
              variant={isResolved ? "outline" : "default"}
              size="sm"
              disabled={setResolved.isPending}
              onClick={toggleResolved}
            >
              {isResolved ? "Reopen" : "Resolve"}
            </Button>
            {isQuarantined ? (
              <Button variant="outline" size="sm" onClick={restore}>
                Restore
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => setConfirmNotRelevant(true)}
              >
                Not relevant
              </Button>
            )}
          </div>
        </div>

        {isResolved && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-300">
            Resolved{thread.resolved_by ? ` by ${thread.resolved_by}` : ""} — off the owed-a-reply
            list. It reopens automatically if a newer message arrives.
          </div>
        )}

        {isQuarantined && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            This thread is marked <span className="font-medium">not relevant</span> — hidden from
            comms views. Restore to bring it back.
          </div>
        )}

        {/* Timeline: messages + notes, interleaved chronologically. */}
        <div className="space-y-3">
          {timeline.map((item) =>
            item.kind === "message" ? (
              <MessageCard
                key={`m:${item.data.public_id}`}
                m={item.data}
                onDeleteVoicemail={setConfirmDeleteVoicemail}
              />
            ) : (
              <NoteCard
                key={`n:${item.data.public_id}`}
                n={item.data}
                onDelete={() => removeNote(item.data.public_id)}
              />
            )
          )}
        </div>

        {/* Reply: sends a real email from support@ (legal@ on a legal thread), captured back into this thread.
            Hidden for voicemail threads: the caller has no email address to answer. */}
        {thread.channel !== "voicemail" && (
          <section className="rounded-lg border border-border bg-surface p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-primary">Reply from {data.reply_from}</h2>
              {replyRecipient && (
                <span className="truncate text-xs text-muted-foreground">to {replyRecipient}</span>
              )}
            </div>
            {replyRecipient ? (
              <>
                <textarea
                  value={replyDraft}
                  onChange={(e) => setReplyDraft(e.target.value)}
                  rows={4}
                  // Edit-locked while an AI draft is generating (a few seconds), so typing
                  // mid-flight can't be silently overwritten when the draft lands (Codex P2).
                  disabled={generateDraft.isPending}
                  placeholder="Write a reply… this sends a real email and is captured in the thread above."
                  className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-primary placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
                  onKeyDown={(e) => {
                    if (
                      (e.metaKey || e.ctrlKey) &&
                      e.key === "Enter" &&
                      replyDraft.trim() &&
                      !generateDraft.isPending
                    )
                      setConfirmReply(true)
                  }}
                />
                <div className="mt-2 flex items-center justify-end gap-3">
                  <span className="text-xs text-muted-foreground">⌘+Enter to send</span>
                  {/* Drafting is support-only (matches the backend boundary). */}
                  {thread.channel === "support" && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={generateDraft.isPending}
                      onClick={doGenerateDraft}
                    >
                      {generateDraft.isPending ? "Generating…" : "Generate draft"}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    disabled={
                      replyToThread.isPending || generateDraft.isPending || replyDraft.trim() === ""
                    }
                    onClick={() => setConfirmReply(true)}
                  >
                    {replyToThread.isPending ? "Sending…" : "Send reply"}
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                No recipient address on this thread, so there's no one to reply to.
              </p>
            )}
          </section>
        )}

        {/* Add a note to the running log. */}
        <section className="rounded-lg border border-border bg-surface p-4">
          <h2 className="mb-2 text-sm font-semibold text-primary">Add a note</h2>
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            rows={2}
            placeholder="Drop context here — it lands in the timeline above…"
            className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-primary placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submitNote()
            }}
          />
          <div className="mt-2 flex items-center justify-end gap-3">
            <span className="text-xs text-muted-foreground">⌘+Enter to save</span>
            <Button
              size="sm"
              disabled={addNote.isPending || noteDraft.trim() === ""}
              onClick={submitNote}
            >
              Add note
            </Button>
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={confirmNotRelevant}
        onOpenChange={setConfirmNotRelevant}
        title="Mark thread not relevant?"
        description={`“${threadLabel}” will be hidden from every comms view. The sender stays a real contact — only this thread is affected. You can undo this or restore it later.`}
        confirmLabel="Not relevant"
        confirmVariant="destructive"
        onConfirm={markNotRelevant}
      />

      <ConfirmDialog
        open={confirmDeleteVoicemail !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteVoicemail(null)
        }}
        title="Delete voicemail?"
        description="Delete this voicemail? The recording and transcript are removed permanently."
        confirmLabel="Delete voicemail"
        confirmVariant="destructive"
        onConfirm={doDeleteVoicemail}
      />

      <ConfirmDialog
        open={confirmReply}
        onOpenChange={setConfirmReply}
        title="Send this reply?"
        description={`This sends a real email from ${data.reply_from} to ${replyRecipient ?? "the contact"} and records it in this thread.`}
        confirmLabel="Send reply"
        onConfirm={doReply}
      />
    </Layout>
  )
}
